/**
 * Как называются Настройки и их значения на трёх языках (тикет T18b, спецификация § 14).
 *
 * Реестр Настроек живёт на сервере: там их перечень, значения по умолчанию и проверки.
 * Здесь — только слова, которыми они названы человеку, и то, каким управлением их
 * показывать. Разделено потому, что сервер обязан отбить неверное значение независимо от
 * того, какое управление нарисовала оболочка, а оболочка обязана назвать Настройку так,
 * чтобы человек понял, — на разных языках это разные слова, а не перевод ключа.
 *
 * Настройка, которой здесь нет, экраном не потеряется: раздел покажет её ключом как есть.
 * Молча спрятать её нельзя — иначе Настройка из более новой версии сервера стала бы
 * невидимой, и человек не смог бы её изменить, хотя она у него уже работает.
 */
import type { Language } from '@core/settings/appearance.js';

export type Translated = Readonly<Record<Language, string>>;

/** Каким управлением показывать Настройку. */
export type SettingForm =
  | { readonly kind: 'choice'; readonly choices: readonly string[] }
  | { readonly kind: 'switch' }
  | { readonly kind: 'volume'; readonly least: number; readonly most: number }
  /** Минуты от полуночи — показываются часами, а хранятся числом. */
  | { readonly kind: 'minutes' }
  /** Часовой пояс: перечень берётся у самого устройства счёта времени, а не из списка. */
  | { readonly kind: 'zone' };

export const SETTING_FORM: Readonly<Record<string, SettingForm>> = {
  theme: { kind: 'choice', choices: ['system', 'light', 'dark', 'high_contrast'] },
  textSize: { kind: 'choice', choices: ['small', 'compact', 'normal', 'large', 'huge'] },
  fontFamily: { kind: 'choice', choices: ['default', 'serif', 'mono', 'readable'] },
  fontWeight: { kind: 'choice', choices: ['normal', 'bold'] },
  lineHeight: { kind: 'choice', choices: ['tight', 'normal', 'loose'] },
  density: { kind: 'choice', choices: ['comfortable', 'compact'] },
  language: { kind: 'choice', choices: ['ru', 'en', 'he'] },
  timeFormat: { kind: 'choice', choices: ['24h', '12h'] },
  weekStart: { kind: 'choice', choices: ['0', '1', '2', '3', '4', '5', '6'] },
  timeZone: { kind: 'zone' },
  reduceMotion: { kind: 'switch' },
  underlineLinks: { kind: 'switch' },
  largeTargets: { kind: 'switch' },
  notificationsEnabled: { kind: 'switch' },
  notifyAbout: { kind: 'choice', choices: ['all', 'mentions', 'none'] },
  quietHoursEnabled: { kind: 'switch' },
  quietFrom: { kind: 'minutes' },
  quietTo: { kind: 'minutes' },
  showMessageText: { kind: 'switch' },
  notifyTaskAssigned: { kind: 'switch' },
  notifyTaskDue: { kind: 'switch' },
  notifyTaskDecided: { kind: 'switch' },
  soundEnabled: { kind: 'switch' },
  soundName: { kind: 'choice', choices: ['classic', 'soft', 'bell', 'knock'] },
  volume: { kind: 'volume', least: 0, most: 100 },
  soundOnSend: { kind: 'switch' },
  sendWith: { kind: 'choice', choices: ['enter', 'ctrl_enter'] },
  linkPreviews: { kind: 'switch' },
  autoLoadMedia: { kind: 'switch' },
  readReceipts: { kind: 'switch' },
  presenceMode: { kind: 'choice', choices: ['auto', 'online', 'away', 'dnd', 'invisible'] },
};

export const SETTING_LABEL: Readonly<Record<string, Translated>> = {
  theme: { ru: 'Тема', en: 'Theme', he: 'ערכת נושא' },
  textSize: { ru: 'Размер текста', en: 'Text size', he: 'גודל טקסט' },
  fontFamily: { ru: 'Шрифт', en: 'Font', he: 'גופן' },
  fontWeight: { ru: 'Толщина шрифта', en: 'Font weight', he: 'עובי הגופן' },
  lineHeight: { ru: 'Межстрочный интервал', en: 'Line spacing', he: 'ריווח שורות' },
  density: { ru: 'Плотность экрана', en: 'Density', he: 'צפיפות המסך' },
  language: { ru: 'Язык', en: 'Language', he: 'שפה' },
  timeFormat: { ru: 'Часы', en: 'Clock', he: 'שעון' },
  weekStart: { ru: 'Неделя начинается с', en: 'Week starts on', he: 'השבוע מתחיל ביום' },
  timeZone: { ru: 'Часовой пояс', en: 'Time zone', he: 'אזור זמן' },
  reduceMotion: { ru: 'Меньше движения', en: 'Reduce motion', he: 'פחות תנועה' },
  underlineLinks: { ru: 'Подчёркивать ссылки', en: 'Underline links', he: 'קו תחתון לקישורים' },
  largeTargets: { ru: 'Крупные кнопки', en: 'Large targets', he: 'כפתורים גדולים' },
  notificationsEnabled: { ru: 'Уведомления', en: 'Notifications', he: 'התראות' },
  notifyAbout: { ru: 'Уведомлять о', en: 'Notify about', he: 'להתריע על' },
  quietHoursEnabled: { ru: 'Тихие часы', en: 'Quiet hours', he: 'שעות שקטות' },
  quietFrom: { ru: 'Тихо с', en: 'Quiet from', he: 'שקט מ' },
  quietTo: { ru: 'Тихо до', en: 'Quiet until', he: 'שקט עד' },
  showMessageText: {
    ru: 'Показывать текст Сообщения',
    en: 'Show message text',
    he: 'הצגת תוכן ההודעה',
  },
  notifyTaskAssigned: { ru: 'Мне назначили Задачу', en: 'Task assigned to me', he: 'הוקצתה לי משימה' },
  notifyTaskDue: { ru: 'Подходит срок Задачи', en: 'Task is due', he: 'מועד המשימה מתקרב' },
  notifyTaskDecided: { ru: 'Задачу приняли или вернули', en: 'Task accepted or returned', he: 'המשימה התקבלה או הוחזרה' },
  soundEnabled: { ru: 'Звук', en: 'Sound', he: 'צליל' },
  soundName: { ru: 'Сигнал', en: 'Alert sound', he: 'צליל התראה' },
  volume: { ru: 'Громкость', en: 'Volume', he: 'עוצמת שמע' },
  soundOnSend: { ru: 'Звук при отправке', en: 'Sound when sending', he: 'צליל בשליחה' },
  sendWith: { ru: 'Отправлять клавишей', en: 'Send with', he: 'שליחה באמצעות' },
  linkPreviews: { ru: 'Показывать ссылки картинкой', en: 'Link previews', he: 'תצוגה מקדימה לקישורים' },
  autoLoadMedia: { ru: 'Загружать вложения сразу', en: 'Load attachments automatically', he: 'טעינת קבצים אוטומטית' },
  readReceipts: { ru: 'Отметки о прочтении', en: 'Read receipts', he: 'אישורי קריאה' },
  presenceMode: { ru: 'Присутствие', en: 'Presence', he: 'נוכחות' },
};

/**
 * Подписи значений. Ключ — «настройка.значение»: одно и то же слово у разных Настроек
 * значит разное, и общий словарь значений однажды подставил бы «обычный» размер шрифта
 * в толщину шрифта.
 */
export const CHOICE_LABEL: Readonly<Record<string, Translated>> = {
  'theme.system': { ru: 'Как в системе', en: 'Match system', he: 'לפי המערכת' },
  'theme.light': { ru: 'Светлая', en: 'Light', he: 'בהירה' },
  'theme.dark': { ru: 'Тёмная', en: 'Dark', he: 'כהה' },
  'theme.high_contrast': { ru: 'Высокий контраст', en: 'High contrast', he: 'ניגודיות גבוהה' },

  'textSize.small': { ru: 'Мелкий', en: 'Small', he: 'קטן' },
  'textSize.compact': { ru: 'Убористый', en: 'Compact', he: 'צפוף' },
  'textSize.normal': { ru: 'Обычный', en: 'Normal', he: 'רגיל' },
  'textSize.large': { ru: 'Крупный', en: 'Large', he: 'גדול' },
  'textSize.huge': { ru: 'Очень крупный', en: 'Very large', he: 'גדול מאוד' },

  'fontFamily.default': { ru: 'Обычный', en: 'Default', he: 'רגיל' },
  'fontFamily.serif': { ru: 'С засечками', en: 'Serif', he: 'עם תגים' },
  'fontFamily.mono': { ru: 'Равноширинный', en: 'Monospace', he: 'רוחב אחיד' },
  'fontFamily.readable': { ru: 'Повышенной читаемости', en: 'Easy to read', he: 'קריא במיוחד' },

  'fontWeight.normal': { ru: 'Обычная', en: 'Normal', he: 'רגיל' },
  'fontWeight.bold': { ru: 'Жирная', en: 'Bold', he: 'מודגש' },

  'lineHeight.tight': { ru: 'Плотный', en: 'Tight', he: 'צפוף' },
  'lineHeight.normal': { ru: 'Обычный', en: 'Normal', he: 'רגיל' },
  'lineHeight.loose': { ru: 'Просторный', en: 'Loose', he: 'מרווח' },

  'density.comfortable': { ru: 'Просторно', en: 'Comfortable', he: 'מרווח' },
  'density.compact': { ru: 'Плотно', en: 'Compact', he: 'צפוף' },

  'language.ru': { ru: 'Русский', en: 'Russian', he: 'רוסית' },
  'language.en': { ru: 'Английский', en: 'English', he: 'אנגלית' },
  'language.he': { ru: 'Иврит', en: 'Hebrew', he: 'עברית' },

  'timeFormat.24h': { ru: '24 часа', en: '24-hour', he: '24 שעות' },
  'timeFormat.12h': { ru: '12 часов', en: '12-hour', he: '12 שעות' },

  'weekStart.0': { ru: 'Воскресенье', en: 'Sunday', he: 'ראשון' },
  'weekStart.1': { ru: 'Понедельник', en: 'Monday', he: 'שני' },
  'weekStart.2': { ru: 'Вторник', en: 'Tuesday', he: 'שלישי' },
  'weekStart.3': { ru: 'Среда', en: 'Wednesday', he: 'רביעי' },
  'weekStart.4': { ru: 'Четверг', en: 'Thursday', he: 'חמישי' },
  'weekStart.5': { ru: 'Пятница', en: 'Friday', he: 'שישי' },
  'weekStart.6': { ru: 'Суббота', en: 'Saturday', he: 'שבת' },

  'notifyAbout.all': { ru: 'Обо всём', en: 'Everything', he: 'הכול' },
  'notifyAbout.mentions': { ru: 'Только упоминания', en: 'Mentions only', he: 'אזכורים בלבד' },
  'notifyAbout.none': { ru: 'Ни о чём', en: 'Nothing', he: 'כלום' },

  'soundName.classic': { ru: 'Обычный', en: 'Classic', he: 'קלאסי' },
  'soundName.soft': { ru: 'Мягкий', en: 'Soft', he: 'רך' },
  'soundName.bell': { ru: 'Колокольчик', en: 'Bell', he: 'פעמון' },
  'soundName.knock': { ru: 'Стук', en: 'Knock', he: 'נקישה' },

  'sendWith.enter': { ru: 'Enter', en: 'Enter', he: 'Enter' },
  'sendWith.ctrl_enter': { ru: 'Ctrl + Enter', en: 'Ctrl + Enter', he: 'Ctrl + Enter' },

  'presenceMode.auto': { ru: 'Само', en: 'Automatic', he: 'אוטומטי' },
  'presenceMode.online': { ru: 'В сети', en: 'Online', he: 'מחובר' },
  'presenceMode.away': { ru: 'Отошёл', en: 'Away', he: 'מרוחק' },
  'presenceMode.dnd': { ru: 'Не беспокоить', en: 'Do not disturb', he: 'נא לא להפריע' },
  'presenceMode.invisible': { ru: 'Невидимка', en: 'Invisible', he: 'בלתי נראה' },
};

/** Название Настройки на языке человека; незнакомая — своим ключом, а не пропуском. */
export const settingLabel = (key: string, language: Language): string =>
  SETTING_LABEL[key]?.[language] ?? key;

/** Название значения; незнакомое — как есть: спрятать его хуже, чем показать непонятным. */
export const choiceLabel = (key: string, value: string, language: Language): string =>
  CHOICE_LABEL[`${key}.${value}`]?.[language] ?? value;
